<template>
  <div
    :class="[
      $style.recordCardWrapper,
      $style[isHidden ? 'hidden' : ''],
      $style[isCurrent ? 'current' : '']
    ]"
    v-bind="setAnchor()"
  >
    <div :class="[$style.recordCard]">
      <jtn-ui-typography :class="$style.number" tag="p" type="font-forum-type-1">
        {{ number }}
      </jtn-ui-typography>
      <jtn-ui-typography
        v-if="content.header"
        :class="$style.title"
        tag="h3"
        type="font-article-quote"
      >
        <div v-html="content.header" />
      </jtn-ui-typography>

      <content-blocks-list
        :class="$style.content"
        :data="content.data"
        :is-post="true"
        :need-check-figure-size="needCheckFigureSize"
      />

      <div :class="$style.footer">
        <jtn-ui-share
          :btn-size="48"
          :btn-margin="8"
          :is-popup="shareOptions.isPopup"
          :socials="shareOptions.socials"
          :popup-position="shareOptions.popupPosition"
          :copy-link="shareOptions.cardLink"
          v-observe-visibility="observerVisibility.getOptions('half')"
          @shared="shareCard($event)"
          @linkCopied="sendShareCardReachGoal('linkCopied')"
        />
      </div>
    </div>
    <slot name="adv" />
  </div>
</template>

<script>
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import { JtnUiTypography, JtnUiShare } from '@jtnews/jtn-ui';
import { ObserverVisibility } from '@jtnews/shared';

import ContentBlocksList from '../../containers/content-blocks-list';

import { StoreMixin } from '../../../../shared/mixins';

const SOCIAL_LINK_TO_METRICS_MAP = {
  vk: 'VK',
  twitter: 'TW',
  ok: 'OK',
  telegram: 'TG',
  wa: 'WA'
};

export default {
  name: 'Card',
  components: {
    JtnUiTypography,
    ContentBlocksList,
    JtnUiShare
  },
  mixins: [StoreMixin],
  props: {
    content: {
      type: Object,
      default() {
        return {};
      }
    },
    headerHeight: {
      type: Number,
      default: 0
    },
    number: {
      type: Number,
      default: 0
    },
    isCurrent: {
      type: Boolean,
      default: false
    },
    isHidden: {
      type: Boolean,
      default: false
    },
    needCheckFigureSize: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      destroy$: new Subject(),
      shareOptions: {
        popupPosition: 'bottom-right',
        socials: Object.keys(SOCIAL_LINK_TO_METRICS_MAP),
        btnSize: 36,
        btnMargin: 6,
        isPopup: true,
        cardLink: ''
      },
      observerVisibility: new ObserverVisibility('half')
    };
  },
  computed: {
    recordId() {
      return this.store.layoutModule.record.data.id;
    },
    regionId() {
      return this.store.regionId;
    },
    utmParams() {
      return this.store.sharingModule.getUtmParams(
        'sharecard',
        `${this.recordId}_${this.content.id}`,
        this.regionId
      );
    }
  },
  mounted() {
    this.setCardLink();

    fromEvent(window, 'scroll')
      .pipe(takeUntil(this.destroy$), debounceTime(200))
      .subscribe(() => this.checkPoints());

    this.observerVisibility.event.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state.hook === 'half' && state.value) {
        this.sendViewCardReachGoal();
      }
    });
  },
  destroyed() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  },
  methods: {
    setCardLink() {
       this.shareOptions.cardLink = this.store.sharingModule.getCardShareLink(this.number, this.utmParams);
    },
    setAnchor() {
      return {
        id: `card_${this.content.id}`
      };
    },
    checkPoints() {
      if (this.$el && this.isCurrentCard()) {
        this.$emit('pointActive', { id: this.content.id, navigatedBy: 'scroll' });
      }
    },
    isCurrentCard() {
      const rect = this.$el.getBoundingClientRect();
      const top = Math.floor(rect.top - this.headerHeight);

      return top <= 0 && Math.abs(top) < rect.height;
    },
    shareCard(socialLink) {
      this.sendShareCardReachGoal(socialLink);

      this.store.sharingModule.shareCard({
        socialTarget: socialLink,
        title: this.content.header,
        cardPosition: this.number,
        utmParams: this.utmParams
      });
    },
    sendShareCardReachGoal(socialLink) {
      const metricsClickValue =
        socialLink === 'linkCopied' ? 'Link' : SOCIAL_LINK_TO_METRICS_MAP[socialLink];

      this.sendNewReachGoal(
        `clickShareCard${metricsClickValue}`,
        `Клик: ${metricsClickValue}`
      );
    },
    sendViewCardReachGoal() {
      this.sendNewReachGoal('viewShareCard', 'Просмотр');
    },
    sendNewReachGoal(goalName, valueName) {
      this.store.analyticsModule.sendNewReachGoal({
        blockType: 'Центральная колонка',
        fieldType: 'Шаринг карточки',
        valueName: {
          [valueName]: this.recordId
        },
        goalName
      });
    }
  }
};
</script>

<style lang="scss" module>
@import 'styles';

.recordCardWrapper {
  display: flex;
  flex-direction: column;

  &.hidden {
    height: 0;
    opacity: 0;
    visibility: hidden;
  }
}

.recordCard {
  position: relative;

  padding: 16px;
  margin: 0 0 16px;

  border: 1px solid $color-surface-200;
  border-radius: 16px;

  .current & {
    border-color: $color-primary-light;
  }

  & .footer {
    display: flex;
    justify-content: flex-end;
  }

  @include on-min-resize($tablet) {
    padding: 24px;
  }
}

.content > div {
  margin-top: 10px;

  @include on-min-resize($tablet) {
    margin-top: 12px;
  }
}

.title {
  margin: 8px 0 16px;

  text-align: center;

  font-size: 17px;
  line-height: 24px;

  @include on-min-resize($mobile) {
    font-size: 21px;
    line-height: 24px;
  }

  @include on-min-resize($tablet) {
    font-size: 24px;
    line-height: 28px;
  }
}

.number {
  font-size: 17px;
  line-height: 24px;
  text-align: center;

  @include on-res($mobile) {
    font-size: 18px;
  }
}
</style>
